import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://aqjprivwouhbavmmhknv.supabase.co';
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY || 'sb_publishable_zJYj0n5HmOqO3Z7oVXH0XQ_xQza6DEj';

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ── Entries ───────────────────────────────────────────────────────────────────
export async function fetchEntries(month) {
  const { data, error } = await supabase
    .from('entries')
    .select('*')
    .eq('month', month)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data.map(dbToEntry);
}

// Needs Action — flagged and not yet resolved.
//
// Two ways to leave this list, because action_type means "excluded from every
// commission total" and cannot be cleared just to mark something reviewed:
//   markActioned / promote  clear action_type — the row becomes an ordinary
//                           entry and starts counting toward totals.
//   dismissDuplicate        stamps dismissed_at and leaves action_type alone —
//                           reviewed, and still excluded from every total.
//
// Both halves of the predicate are load-bearing. Dropping the dismissed_at
// clause is the bug this column was added for: dismissals lived in React state
// only, so entry 1620 came back on every reload. See src/dismissal.js.
export async function fetchUnhandledActions() {
  const { data, error } = await supabase
    .from('entries')
    .select('*')
    .not('action_type', 'is', null)
    .is('dismissed_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data.map(dbToEntry);
}

export async function checkDuplicateInvoice(invoice) {
  if (!invoice) return null;
  const { data, error } = await supabase
    .from('entries')
    .select('id, rep, month, amount, sub_total, commissionable_amount, customer, action_type')
    .eq('invoice', String(invoice).trim())
    .limit(5);
  if (error) throw error;
  if (!data || data.length === 0) return null;
  // Only flag against "real" entries — ignore already-flagged duplicates/credit memos
  const real = data.find(d => !d.action_type || d.action_type === '3rd_party');
  return real || null;
}

export async function insertEntry(entry) {
  const { data, error } = await supabase
    .from('entries')
    .insert([entryToDb(entry)])
    .select()
    .single();
  if (error) throw error;
  return dbToEntry(data);
}

export async function deleteEntryById(id) {
  const { error } = await supabase.from('entries').delete().eq('id', id);
  if (error) throw error;
}

export async function updateEntry(id, fields) {
  const { data, error } = await supabase
    .from('entries')
    .update(entryToDb(fields))
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return dbToEntry(data);
}

// Cross-month search for the Sales Log search bar — runs ILIKE against
// invoice / customer / rep across the full entries table. Returns up to 500
// most-recent rows so the UI doesn't have to handle unbounded result sets.
// Ledger context for the Comm Review tab.
//
// A commission flag is a CLAIM. Every figure on it — amount, customer, even
// the invoice number on a missing_invoice flag — is what the rep typed, and
// the admin has until now been shown only that, with nothing to compare it
// against (AUDIT/40 item 39). The two flag types need opposite lookups:
//
//   'entry'            has an entry_id. Look up that row to show the LEDGER
//                      amount beside the rep's disputed figure. The two
//                      differing is the normal case — that is the dispute —
//                      so they are labelled rather than reconciled.
//   'missing_invoice'  has no entry_id by design. Look the INVOICE up instead
//                      and answer the only question that matters: does this
//                      sale exist, and if so whose is it?
//
// Exact invoice match, deliberately. A suffixed row (-DUP, -ADD) is by
// definition an excluded duplicate or an adjustment rather than the sale, so
// not matching one is correct here — a promoted duplicate has its suffix
// stripped and matches plainly.
//
// Two batched queries rather than one per flag: the flag list is small but
// unbounded, and per-row lookups in a render path is how a summary view
// becomes a hundred requests.
export async function fetchFlagLedgerContext(flags) {
  const COLS = 'id, rep, invoice, customer, month, sub_total, amount, commissionable_amount, action_type';
  const list = flags || [];

  const ids = [...new Set(
    list.filter(f => f && f.entry_id !== null && f.entry_id !== undefined).map(f => f.entry_id)
  )];
  const invoices = [...new Set(
    list.filter(f => f && f.flag_type === 'missing_invoice' && f.invoice)
        .map(f => String(f.invoice).trim())
        .filter(Boolean)
  )];

  const [byIdRes, byInvRes] = await Promise.all([
    ids.length      ? supabase.from('entries').select(COLS).in('id', ids)           : Promise.resolve({ data: [], error: null }),
    invoices.length ? supabase.from('entries').select(COLS).in('invoice', invoices) : Promise.resolve({ data: [], error: null }),
  ]);
  if (byIdRes.error)  throw byIdRes.error;
  if (byInvRes.error) throw byInvRes.error;

  const byEntryId = {};
  for (const e of byIdRes.data || []) byEntryId[e.id] = e;

  // A list per invoice, not a single row: the same number can legitimately
  // appear more than once (a split invoice across two reps is exactly the
  // case a missing_invoice flag is raised about).
  const byInvoice = {};
  for (const e of byInvRes.data || []) {
    const k = String(e.invoice);
    if (!byInvoice[k]) byInvoice[k] = [];
    byInvoice[k].push(e);
  }

  return { byEntryId, byInvoice };
}

export async function searchEntriesGlobal(query) {
  const q = (query || '').trim();
  if (!q) return [];
  // Escape PostgREST or-filter delimiters and ILIKE wildcards. The or() syntax
  // splits on commas and parentheses, so any of those in the user's input
  // would corrupt the filter; ILIKE treats % and _ as wildcards.
  const safe = q.replace(/[,()*\\%_]/g, (c) => '\\' + c);
  const pattern = `%${safe}%`;
  const { data, error } = await supabase
    .from('entries')
    .select('*')
    .or(`invoice.ilike.${pattern},customer.ilike.${pattern},rep.ilike.${pattern}`)
    .order('date_str', { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data || []).map(dbToEntry);
}

// ── Month settings ────────────────────────────────────────────────────────────
export async function fetchMonthSettings(month) {
  const { data, error } = await supabase
    .from('month_settings')
    .select('*')
    .eq('month', month)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function upsertMonthSettings(month, priorYearTotal) {
  const { error } = await supabase
    .from('month_settings')
    .upsert({ month, prior_year_total: priorYearTotal, updated_at: new Date().toISOString() }, { onConflict: 'month' });
  if (error) throw error;
}

// ── Adjustments ───────────────────────────────────────────────────────────────
export async function fetchAdjustments(month) {
  const { data, error } = await supabase
    .from('adjustments')
    .select('*')
    .eq('month', month)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}

export async function insertAdjustment(adj) {
  const { data, error } = await supabase
    .from('adjustments')
    .insert([adj])
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteAdjustment(id) {
  const { error } = await supabase.from('adjustments').delete().eq('id', id);
  if (error) throw error;
}

// Patch arbitrary columns on an adjustment row. Used by the Sales Log inline
// tier picker on sales_reversal rows that pre-date the tier column — admin
// assigns the right tier and we PATCH it so the commission formula can
// resolve correctly thereafter.
export async function updateAdjustment(id, fields) {
  const { data, error } = await supabase
    .from('adjustments')
    .update(fields)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Send cycle ────────────────────────────────────────────────────────────────
export async function fetchSendCycle(month) {
  const { data, error } = await supabase
    .from('send_cycles')
    .select('*')
    .eq('month', month)
    .order('sent_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function insertSendCycle(month, entryIds, sentCount, subjectLine) {
  const { data, error } = await supabase
    .from('send_cycles')
    .insert([{
      month,
      entry_ids: entryIds.map(Number),
      sent_at: new Date().toISOString(),
      sent_count: sentCount,
      subject_line: subjectLine||"",
    }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function clearSendCycle(month) {
  const { error } = await supabase
    .from('send_cycles')
    .delete()
    .eq('month', month);
  if (error) throw error;
}

// ── Handled actions ───────────────────────────────────────────────────────────
// Insert a record when any Needs Action item is marked handled
export async function insertHandledAction(entry, flaggedItems, handledBy, notes) {
  const { data, error } = await supabase
    .from('handled_actions')
    .insert([{
      entry_id:      entry.id,
      invoice:       entry.invoice,
      customer:      entry.customer,
      rep:           entry.rep,
      amount:        entry.amount,
      month:         entry.month,
      flagged_items: flaggedItems,
      handled_at:    new Date().toISOString(),
      handled_by:    handledBy || null,
      notes:         notes || null,
      action_type:   entry.actionType || '3rd_party',
    }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Fetch all handled actions — UI filters by display window
export async function fetchHandledActions() {
  const { data, error } = await supabase
    .from('handled_actions')
    .select('*')
    .order('handled_at', { ascending: false });
  if (error) throw error;
  return data;
}

// Delete a handled action record (used when undoing)
export async function deleteHandledAction(id) {
  const { error } = await supabase.from('handled_actions').delete().eq('id', id);
  if (error) throw error;
}

// The dismissal log for one entry, if it has one.
//
// Only called after an insert loses the race to the partial unique index —
// another tab dismissed the same item first, and this tab needs the winner's
// row to show in Completed / handled rather than an error for work that did in
// fact get done. Earliest first, matching findDismissalLog and the migration.
export async function fetchDismissalLog(entryId) {
  const { data, error } = await supabase
    .from('handled_actions')
    .select('*')
    .eq('entry_id', entryId)
    .eq('action_type', 'duplicate')
    .order('handled_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// ── Month confirmations ──────────────────────────────────────────────────────
// Rep sign-off on monthly commission totals. RLS scopes selects/inserts to the
// caller's own rep_code (admins also have a select-all policy). Schema +
// policies live in supabase_schema.sql.

// Fetch confirmation for a specific rep and month. Returns null when no row
// exists (PGRST116 = "single() got 0 rows"); other errors propagate.
export async function fetchMonthConfirmation(rep, month) {
  const { data, error } = await supabase
    .from('month_confirmations')
    .select('*')
    .eq('rep', rep)
    .eq('month', month)
    .single();
  if (error && error.code !== 'PGRST116') throw error;
  return data;
}

// DEAD. Nothing calls this, and nothing should.
//
// ⚠ This comment used to say "no longer called" and that was FALSE from the
// day it was written until 2026-09-01: the previous-month banner in App.js
// called it the whole time. The claim is what made the split invisible — two
// rep-facing confirm buttons writing two different tables, one of them with no
// snapshot, and a comment asserting that could not happen.
//
// What it cost: on 2026-09-01 Andrew, Jose and Roman confirmed August through
// the toolbox (which writes commission_confirmations) while the admin
// Confirmation Status grid read month_confirmations, so all three showed as
// "Not confirmed". Jake, who used both buttons fourteen minutes apart, has an
// August row in each table.
//
// The banner now calls confirm_commission_month() like everything else — RULED
// 2026-09-01, one path for all confirmations. This function is retained only
// so a future reader finds this note instead of reinventing the write.
//
// DO NOT REINSTATE IT. A confirmation without a snapshot cannot be checked for
// staleness, which is the entire reason commission_confirmations exists. If you
// need to write a confirmation, call insertCommissionConfirmation below.
//
// The month_confirmations TABLE stays — it is still read for pre-snapshot
// history, and those rows are real confirmations that must not read as never
// confirmed. Reading it is fine. Writing it is not.
export async function insertMonthConfirmation(rep, month, email) {
  const { data, error } = await supabase
    .from('month_confirmations')
    .insert({ rep, month, confirmed_by_email: email })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── commission_confirmations — append-only, snapshot-bearing ────────────────

/** Live (non-superseded) confirmations for a month. Admin grid + rep badge. */
export async function fetchCommissionConfirmations(month) {
  const { data, error } = await supabase
    .from('commission_confirmations')
    .select('*')
    .eq('month', month)
    .eq('superseded', false)
    .order('confirmed_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/**
 * Record a confirmation.
 *
 * One call to confirm_commission_month() — see sql/commission_confirmations_atomic.sql.
 * The supersede and the insert still happen in that order and never the reverse,
 * but they now happen inside one transaction on the server, so a failed insert
 * can no longer leave a rep-month with its prior snapshot retired and nothing
 * live in its place. Two round trips could only narrow that window; they could
 * not close it.
 *
 * Rows are never deleted and never rewritten beyond the superseded flag. The
 * prior confirmation is the evidence that it happened.
 *
 * Returns the inserted row whole — the function RETURNS commission_confirmations,
 * so this is the same shape .insert().select().single() gave, and App.js splices
 * it into commConfirms optimistically.
 *
 * Throws on failure. Callers must distinguish code '23505': the partial unique
 * index on (month, rep) WHERE NOT superseded fired, which means a concurrent
 * confirmer — the rep here, an admin on-behalf in the toolbox — got there first.
 * That caller's transaction rolled back entirely and the winner's confirmation
 * stands. Retrying hits the same wall; the fix is to refresh.
 */
export async function insertCommissionConfirmation(rep, month, email, snapshot) {
  const { data, error } = await supabase.rpc('confirm_commission_month', {
    p_rep: rep,
    p_month: month,
    p_confirmed_by: email || null,
    p_snapshot: snapshot,
    p_options: {},
  });
  if (error) throw error;
  return data;
}

// Stamp approved_at on every entry for the CALLER's own rep for `month`, where
// not already approved. Once stamped, the row is locked in the Sales Log — no
// edit / no delete. Corrections happen via a fresh manual entry in the current
// month.
//
// NO rep ARGUMENT, DELIBERATELY. This was a bare UPDATE scoped by .eq('rep',…)
// in the client, which worked only because entries carried
// `{authenticated} true` on UPDATE — i.e. any signed-in employee could stamp
// any rep's rows, and the scoping was a client-side convention rather than a
// rule. Tranche D (toolbox supabase/migrations/062) takes entries UPDATE down
// to current_is_admin(), so this path needed a sanctioned route or it would
// have started failing SILENTLY: a denied UPDATE matches zero rows, it does not
// error, so the old call would have returned [] and stamped nothing with no
// message anywhere.
//
// approve_own_month_entries() (toolbox supabase/migrations/061) is that route.
// It is SECURITY DEFINER and resolves the rep SERVER-SIDE from the JWT, so the
// caller cannot name a rep at all. The alternative — an RLS carve-out
// USING (rep = current_rep_code_safe()) — was rejected because RLS is
// row-level, not column-level: it would have let a rep update `amount` and
// `tier` on their own rows too.
//
// Kept SEPARATE from confirm_commission_month() on purpose. The rep-confirm
// path approves; the admin on-behalf path deliberately does not
// (certags-toolbox ActionCenter.jsx:454). Folding the approve into the confirm
// function would erase that asymmetry — see the "NO bulkApproveEntries" section
// of sql/commission_confirmations_atomic.sql.
//
// Throws 42501 if the signed-in email is not an active rep in `employees`.
// That is a real misconfiguration, not a no-op: it means the DB cannot resolve
// the person the client already believes is a rep.
export async function bulkApproveEntries(month) {
  const { data, error } = await supabase.rpc('approve_own_month_entries', {
    p_month: month,
  });
  if (error) throw error;
  return (data || []).map(dbToEntry);
}

// Admin-only — RLS allows ccollard / tracy to see all rows.
export async function fetchAllMonthConfirmations(month) {
  const { data, error } = await supabase
    .from('month_confirmations')
    .select('*')
    .eq('month', month)
    .order('confirmed_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// ── Data mappers ──────────────────────────────────────────────────────────────
// `amount` is kept as a mirror of `commissionable_amount` for backward compat with
// the existing getComm(e.amount, …) call sites scattered through App.js. Code that
// wants the gross sales total should read `subTotal` instead.
//
// IMPORTANT: every output field is conditional on the corresponding input key
// being present. Partial updates (markActioned, undoHandledAction) pass only a
// few fields — we must NOT compute defaults for the others, or they would be
// PATCHed as null/0 and clobber the row.
function entryToDb(e) {
  const has = (k) => Object.prototype.hasOwnProperty.call(e, k);
  const out = {};

  if (has('rep'))             out.rep               = e.rep;
  if (has('invoice'))         out.invoice           = e.invoice;
  if (has('customer'))        out.customer          = e.customer;
  if (has('tier'))            out.tier              = e.tier;
  if (has('month'))           out.month             = e.month;
  if (has('dateStr'))         out.date_str          = e.dateStr;
  if (has('isZero'))          out.is_zero           = e.isZero || false;
  if (has('isComplyPro'))     out.is_comply_pro     = e.isComplyPro || false;
  if (has('isPrinter'))       out.is_printer        = e.isPrinter || false;
  if (has('needs3rdParty'))   out.needs_3rd_party   = e.needs3rdParty || false;
  if (has('flaggedItems'))    out.flagged_items     = e.flaggedItems || [];
  if (has('isLoaner'))        out.is_loaner         = e.isLoaner || false;
  if (has('isNewAccount'))    out.is_new_account    = e.isNewAccount || false;
  if (has('actionType'))      out.action_type       = e.actionType;
  if (has('actionNote'))      out.action_note       = e.actionNote;
  // Presence-keyed like the rest, which is what lets dismiss set it and undo
  // clear it while both leave action_type — and therefore every total — alone.
  if (has('dismissedAt'))     out.dismissed_at      = e.dismissedAt;
  // The status comment — "what is the situation right now", as opposed to
  // action_note's "what was done to close this". Presence-keyed for the same
  // reason as dismissedAt: statusCommentPatch always sends all three keys, so
  // clearing the text nulls the byline with it — while every OTHER patch in
  // the app omits them entirely, which is what makes a comment survive
  // dismiss, mark-handled, promote and undo untouched.
  if (has('statusComment'))   out.status_comment    = e.statusComment;
  if (has('statusCommentAt')) out.status_comment_at = e.statusCommentAt;
  if (has('statusCommentBy')) out.status_comment_by = e.statusCommentBy;
  if (has('rationale'))       out.rationale         = e.rationale;
  if (has('isCredit'))        out.is_credit         = e.isCredit;
  if (has('approvedAt'))      out.approved_at       = e.approvedAt;

  // Amount columns: only write if at least one amount-related field is present.
  // For partial updates (e.g. clearing actionType only), these MUST be omitted
  // entirely so PostgREST doesn't overwrite the row's existing amounts with NaN/null.
  if (has('amount') || has('subTotal') || has('feeTotal') || has('commissionableAmount')) {
    // Resolve subTotal: prefer explicit subTotal, fall back to legacy `amount`,
    // fall back to commissionableAmount (only if neither subTotal nor amount given).
    const subTotal =
      (e.subTotal != null) ? e.subTotal :
      (e.amount   != null) ? e.amount   :
      (e.commissionableAmount != null ? e.commissionableAmount : null);
    const feeTotal = (e.feeTotal != null) ? e.feeTotal : 0;
    const commissionableAmount =
      (e.commissionableAmount != null) ? e.commissionableAmount :
      (subTotal != null ? subTotal - feeTotal : null);

    if (subTotal             != null) out.sub_total             = subTotal;
    if (commissionableAmount != null) out.commissionable_amount = commissionableAmount;
    if (commissionableAmount != null) out.amount                = commissionableAmount;
    out.fee_total = feeTotal;
    if (!has('isCredit') && subTotal != null) out.is_credit = subTotal < 0;
  }

  return out;
}

function dbToEntry(d) {
  // Old (un-backfilled) rows have NULL sub_total/fee_total/commissionable_amount.
  // Fall back to `amount` so consumers always have something sensible to read.
  const amount               = parseFloat(d.amount);
  const subTotal             = d.sub_total != null             ? parseFloat(d.sub_total)             : amount;
  const feeTotal             = d.fee_total != null             ? parseFloat(d.fee_total)             : 0;
  const commissionableAmount = d.commissionable_amount != null ? parseFloat(d.commissionable_amount) : amount;
  return {
    id:                   Number(d.id),
    rep:                  d.rep,
    invoice:              d.invoice,
    customer:             d.customer,
    amount:               amount,
    subTotal:             subTotal,
    feeTotal:             feeTotal,
    commissionableAmount: commissionableAmount,
    isCredit:             d.is_credit || false,
    rationale:            d.rationale || null,
    tier:                 d.tier,
    month:                d.month,
    dateStr:              d.date_str,
    isZero:               d.is_zero,
    isComplyPro:          d.is_comply_pro,
    isPrinter:            d.is_printer,
    needs3rdParty:        d.needs_3rd_party,
    flaggedItems:         d.flagged_items || [],
    isLoaner:             d.is_loaner || false,
    isNewAccount:         d.is_new_account || false,
    actionType:           d.action_type || null,
    actionNote:           d.action_note || null,
    dismissedAt:          d.dismissed_at || null,
    statusComment:        d.status_comment || null,
    statusCommentAt:      d.status_comment_at || null,
    statusCommentBy:      d.status_comment_by || null,
    approvedAt:           d.approved_at || null,
  };
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export async function login(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data.user;
}

export async function logout() {
  await supabase.auth.signOut();
}

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}
